import {
  MainMetaMap,
  MainMetaMapSubTopic,
  MainMetaMapTopic,
} from "@tsTypes/meta-schema";
import { Question } from "@tsTypes/question-schema";
import { findRelatedSubjectMetaFromSubTopicDocumentId } from "./dataFetching";
import { useMainMetaMap } from "@/app/components/CoreDataProvider";

/**
 * Checks if a question is a multiple choice question.
 * @param {Question} question - The question to check
 * @returns {boolean} True if the question is multiple choice, false otherwise
 */
export function checkIfMultipleChoiceQuestion(question: Question) {
  return ["questions.text-options", "questions.image-options"].includes(
    question.QuestionStyle[0].__component
  );
}

export type GroupedSubtopicsBySubject = Record<
  string,
  {
    title: string;
    topics: Record<
      string,
      {
        title: string;
        subtopics: (MainMetaMapSubTopic & { id: string })[];
      }
    >;
  }
>;

export function groupSubtopicsBySubjectAndTopic(
  mainMetaMap: MainMetaMap
): GroupedSubtopicsBySubject {
  return Object.entries(mainMetaMap).reduce((acc, [id, value]) => {
    if ("topicDocumentId" in value) {
      const subtopic = value as MainMetaMapSubTopic;
      const topic = mainMetaMap[subtopic.topicDocumentId] as MainMetaMapTopic;
      const subjectId = topic.subjectDocumentId;
      const subject = mainMetaMap[subjectId];

      if (!acc[subjectId]) {
        acc[subjectId] = {
          title: "title" in subject ? subject.title : "",
          topics: {},
        };
      }

      if (!acc[subjectId].topics[subtopic.topicDocumentId]) {
        acc[subjectId].topics[subtopic.topicDocumentId] = {
          title: `${topic.number}: ${topic.title}`,
          subtopics: [],
        };
      }

      acc[subjectId].topics[subtopic.topicDocumentId].subtopics.push({
        id,
        ...subtopic,
      });
    }

    return acc;
  }, {} as GroupedSubtopicsBySubject);
}

export function getAllTopicDocumentIdsFromMainMetaMap(
  mainMetaMap: MainMetaMap
) {
  return Object.entries(mainMetaMap)
    .filter(
      ([_, value]) =>
        "subjectDocumentId" in value && "topicDocumentId" in value === false
    )
    .map(([id]) => id);
}

export function getSubTopicDocumentIdsForTopic(
  mainMetaMap: MainMetaMap,
  topicDocumentId: string
) {
  return Object.entries(mainMetaMap)
    .filter(([_, value]) => {
      if (
        "topicDocumentId" in value &&
        value.topicDocumentId === topicDocumentId
      ) {
        return true;
      }
      return false;
    })
    .map(([id]) => id);
}

/**
 * Admin tools load questions in per-topic Strapi batches (filter: question relates
 * to at least one subtopic in that topic). The same draft may appear in several
 * batches when it is linked to subtopics in different topics. When aggregating
 * counts or ID lists per subtopic, only consider links whose subtopic belongs to
 * the current topic’s `subtopicDocumentIds` list — same constraint as
 * `getAdminToolResourceCountsBySubTopic` in dataFetching. AI question generation
 * uses `convertQuestionsByTopicToQuestionTotalsBySubTopic` (testBuilder), which
 * attributes each in-batch question to one subtopic via `.find()` for difficulty totals.
 */
export function forEachSubtopicOfQuestionInTopic(
  question: { RelatedSubTopics?: { documentId: string }[] | undefined },
  topicSubtopicDocumentIds: string[],
  fn: (subtopicDocumentId: string) => void,
): void {
  const inTopic = new Set(topicSubtopicDocumentIds);
  for (const st of question.RelatedSubTopics || []) {
    const id = st?.documentId;
    if (id != null && inTopic.has(id)) fn(id);
  }
}

export function getSubjectTitleForSubtopic(
  mainMetaMap: MainMetaMap,
  subtopicDocumentId: string
) {
  const subtopic = mainMetaMap[subtopicDocumentId] as
    | MainMetaMapSubTopic
    | undefined;
  if (!subtopic) return "";
  const topic = mainMetaMap[subtopic.topicDocumentId] as
    | MainMetaMapTopic
    | undefined;
  if (!topic) return "";
  const subject = mainMetaMap[topic.subjectDocumentId];
  if (!subject || !("title" in subject)) return "";
  return subject.title;
}

/**
 * Shuffles an array using the Fisher-Yates algorithm.
 * @param {any[]} array - The array to shuffle
 * @returns {any[]} A new array with the elements in random order
 */
export function shuffleArray(array: any[]) {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

/**
 * Calculates the average of an array of numbers.
 * @param {number[]} numberArray - Array of numbers to average
 * @returns {number} The average rounded to 2 decimal places, or 0 if array is empty
 */
export function averageArray(numberArray: number[]) {
  let total = 0;
  numberArray.map((n) => (total += n));
  return total === 0 ? 0 : roundTo2DP(total / numberArray.length);
}

/**
 * Shuffles questions while maintaining separation between multiple choice and long answer questions.
 * @param {Question[]} questionArray - Array of questions to shuffle
 * @returns {Question[]} Shuffled array with MC questions first, followed by LA questions
 */
export function shuffleQuestionsWithSegmentation(questionArray: Question[]) {
  let MC = [];
  let LA = [];
  for (const q of questionArray) {
    if (checkIfMultipleChoiceQuestion(q)) {
      MC.push(q);
    } else {
      LA.push(q);
    }
  }
  MC = shuffleArray(MC);
  LA = shuffleArray(LA);
  return [...MC, ...LA] as Question[];
}

/**
 * Joins an array of strings into a natural language list with commas and "and".
 * @param {string[]} array - Array of strings to join
 * @returns {string} Formatted string (e.g., "item1, item2, and item3")
 */
export function joinItemsToList(array: string[]) {
  if (array.length === 1) {
    return array[0];
  }
  var joinedString = array.slice(0, -1).join(", ");
  var lastItem = array[array.length - 1];
  joinedString += ", and " + lastItem;
  return joinedString;
}

/**
 * Returns a random arithmetic operator (either + or -).
 * @returns {string} "+" or "-" with equal probability
 */
export function getRandomOperator() {
  const randomNumber = Math.random();
  if (randomNumber < 0.5) {
    return "+";
  } else {
    return "-";
  }
}

/**
 * Updates an average with new values.
 * @param {number} currentAverage - The current average
 * @param {number} currentCount - The current count
 * @param {number[]} newValues - The new values to add to the average
 * @returns {number} The new average
 */
export function updateAverageWithNewValues(
  currentAverage: number,
  currentCount: number,
  newValues: number[]
): number {
  const currentTotal = currentAverage * currentCount;
  const newTotal = currentTotal + sumArray(newValues);
  const newCount = currentCount + newValues.length;
  if (newCount === 0) {
    return 0.0;
  }
  const newAverage = newTotal / newCount;
  return roundTo1DP(newAverage);
}

/**
 * Sums an array of numbers.
 * @param {number[]} values - Array of numbers to sum
 * @returns {number} Sum of the numbers
 */
export function sumArray(values: number[]): number {
  return values.reduce((a, b) => a + b, 0);
}

/**
 * Rounds a number to 2 decimal places.
 * @param {number} number - The number to round
 * @returns {number} Rounded number
 */
export function roundTo2DP(number: number) {
  return Math.round(number * 100) / 100;
}

/**
 * Rounds a number to 1 decimal place.
 * @param {number} number - The number to round
 * @returns {number} Rounded number
 */
export function roundTo1DP(number: number) {
  return Math.round(number * 10) / 10;
}

/**
 * Rounds a number to the nearest whole number
 * @param {number} number - The number to round
 * @returns {number} Rounded number
 */
export function roundToWhole(number: number) {
  return Math.round(number);
}

/**
 * Converts seconds to a formatted time string.
 * @param {number} seconds - Number of seconds to convert
 * @param {true} [withLetters] - If true, returns format "Xm Ys", otherwise returns "XX:YY"
 * @returns {string} Formatted time string
 */
export function convertSecondsToMinSec(seconds: number, withLetters?: true) {
  let minutes: string | number = Math.floor(seconds / 60);
  let remainingSeconds: string | number = Math.floor(seconds % 60);
  if (!withLetters) {
    return `${minutes < 10 ? `0${minutes}` : minutes}:${
      remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds
    }`;
  }
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Smoothly scrolls the window to the top.
 * @returns {void}
 */
export function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}

/**
 * Returns the core theme colors used throughout the application.
 * @returns {object} Object containing color palettes for various themes
 */
export function getCoreThemeColors() {
  return {
    darkblue: [
      "#005CD5",
      "#0051BB",
      "#0047A4",
      "#003E90",
      "#00377E",
      "#00306F",
      "#002A61",
      "#002453",
      "#001F48",
      "#001B3E",
    ],
    blue: [
      "#DDF2FD",
      "#A6DCFA",
      "#76C9F7",
      "#4CB9F4",
      "#26AAF2",
      "#0E9BE9",
      "#0C88CC",
      "#0A75AF",
      "#096597",
      "#085782",
    ],
    lightblue: [
      "#DDF7FF",
      "#A2E9FF",
      "#6FDDFF",
      "#42D3FF",
      "#1BC9FF",
      "#00BDF7",
      "#00A3D4",
      "#008CB7",
      "#00789D",
      "#006787",
    ],
    biology: [
      "#fbfffd",
      "#b5ffd1",
      "#6bffa4",
      "#21ff76",
      "#00D652",
      "#00b244",
      "#008f37",
      "#006b29",
      "#00471b",
      "#00240e",
    ],
    // "biology 2025": [
    //   "#fbfffd",
    //   "#b5ffd1",
    //   "#6bffa4",
    //   "#21ff76",
    //   "#00D652",
    //   "#00b244",
    //   "#008f37",
    //   "#006b29",
    //   "#00471b",
    //   "#00240e",
    // ],
    chemistry: [
      "#fffafb",
      "#fec8d6",
      "#fe91ae",
      "#fd5a85",
      "#FC235D",
      "#ec0341",
      "#bd0334",
      "#8e0227",
      "#5e011a",
      "#2f010d",
    ],
    // "chemistry 2025": [
    //   "#fffafb",
    //   "#fec8d6",
    //   "#fe91ae",
    //   "#fd5a85",
    //   "#FC235D",
    //   "#ec0341",
    //   "#bd0334",
    //   "#8e0227",
    //   "#5e011a",
    //   "#2f010d",
    // ],
    physics: [
      "#fefdff",
      "#edd5fc",
      "#daabfa",
      "#c882f7",
      "#B658F4",
      "#9f24f1",
      "#830ed0",
      "#620a9c",
      "#410768",
      "#210334",
    ],
    // "physics 2025": [
    //   "#fefdff",
    //   "#edd5fc",
    //   "#daabfa",
    //   "#c882f7",
    //   "#B658F4",
    //   "#9f24f1",
    //   "#830ed0",
    //   "#620a9c",
    //   "#410768",
    //   "#210334",
    // ],
    maths: [
      "#FFFDFA",
"#FFECD3",
"#FFD7A1",
"#FFC375",
"#FFB24D",
"#FFA125",
"#FF9202",
"#C06F00",
"#9E5A00",
"#7F4700",
    ],
    gold: [
      "#FFFCEE",
      "#FFEFB1",
      "#FFE57C",
      "#FFDB4E",
      "#FFD325",
      "#FFCC01",
      "#DCB000",
      "#BD9700",
      "#A38200",
      "#8C7000",
    ],
    correct: [
      "#F2FBED",
      "#D0EFBC",
      "#B2E592",
      "#97DC6D",
      "#80D44C",
      "#6CCC31",
      "#5DAF2A",
      "#509724",
      "#45821F",
      "#3B701B",
    ],
    incorrect: [
      "#FFC7C7",
      "#FF9090",
      "#FF5F5F",
      "#FF3434",
      "#FF0E0E",
      "#EC0000",
      "#CB0000",
      "#AF0000",
      "#960000",
      "#810000",
    ],
  } as {
    [colorKey: string]: [
      string | undefined,
      string | undefined,
      string | undefined,
      string | undefined,
      string | undefined,
      string | undefined,
      string | undefined,
      string | undefined,
      string | undefined,
      string | undefined
    ];
  };
}
